import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, getSessionToken } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { User, Mail, Target, LogOut, Check, Loader2, Calendar, Package, Building2, Phone, MapPin, FileText, Briefcase, Pencil, X, Save, Shield, Sparkles } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import PhoneInput from './PhoneInput';
import { setSessionToken } from '../utils/sessionStorage';

const GOALS = [
  { id: 'weight_loss', icon: '⚖️', en: 'Weight Loss', es: 'Pérdida de Peso', ru: 'Потеря Веса',
    de: 'Gewichtsverlust', fr: 'Perte de poids', it: 'Perdita di peso', pl: 'Utrata wagi', nl: 'Gewichtsverlies', pt: 'Perda de peso' },
  { id: 'muscle_building', icon: '💪', en: 'Muscle Building', es: 'Construcción Muscular', ru: 'Набор Массы',
    de: 'Muskelaufbau', fr: 'Prise de muscle', it: 'Crescita muscolare', pl: 'Budowa mięśni', nl: 'Spieropbouw', pt: 'Ganho muscular' },
  { id: 'anti_aging', icon: '✨', en: 'Anti-Aging', es: 'Anti-Envejecimiento', ru: 'Антивозрастное',
    de: 'Anti-Aging', fr: 'Anti-âge', it: 'Anti-invecchiamento', pl: 'Przeciwstarzeniowe', nl: 'Anti-veroudering', pt: 'Antienvelhecimento' },
  { id: 'cognitive_enhancement', icon: '🧠', en: 'Cognitive Enhancement', es: 'Mejora Cognitiva', ru: 'Когнитивное',
    de: 'Kognitive Leistung', fr: 'Performance cognitive', it: 'Potenziamento cognitivo', pl: 'Funkcje poznawcze', nl: 'Cognitieve verbetering', pt: 'Aprimoramento cognitivo' },
  { id: 'recovery_healing', icon: '🩹', en: 'Recovery & Healing', es: 'Recuperación', ru: 'Восстановление',
    de: 'Regeneration', fr: 'Récupération', it: 'Recupero', pl: 'Regeneracja', nl: 'Herstel', pt: 'Recuperação' },
  { id: 'immune_support', icon: '🛡️', en: 'Immune Support', es: 'Apoyo Inmunológico', ru: 'Иммунитет',
    de: 'Immunsystem', fr: 'Soutien immunitaire', it: 'Supporto immunitario', pl: 'Wsparcie odporności', nl: 'Immuunondersteuning', pt: 'Suporte imunológico' },
  { id: 'hormonal_balance', icon: '⚡', en: 'Hormonal Balance', es: 'Equilibrio Hormonal', ru: 'Гормональный',
    de: 'Hormonelles Gleichgewicht', fr: 'Équilibre hormonal', it: 'Equilibrio ormonale', pl: 'Równowaga hormonalna', nl: 'Hormonale balans', pt: 'Equilíbrio hormonal' },
  { id: 'skin_health', icon: '🌸', en: 'Skin Health', es: 'Salud de la Piel', ru: 'Здоровье Кожи',
    de: 'Hautgesundheit', fr: 'Santé de la peau', it: 'Salute della pelle', pl: 'Zdrowie skóry', nl: 'Huidgezondheid', pt: 'Saúde da pele' }
];

const ORGANIZATION_TYPES = [
  { id: 'laboratory', en: 'Research Laboratory', es: 'Laboratorio de Investigación', ru: 'Исследовательская Лаборатория',
    de: 'Forschungslabor', fr: 'Laboratoire de recherche', it: 'Laboratorio di ricerca', pl: 'Laboratorium badawcze', nl: 'Onderzoekslaboratorium', pt: 'Laboratório de Pesquisa' },
  { id: 'clinic', en: 'Medical Clinic', es: 'Clínica Médica', ru: 'Медицинская Клиника',
    de: 'Medizinische Klinik', fr: 'Clinique médicale', it: 'Clinica medica', pl: 'Klinika medyczna', nl: 'Medische kliniek', pt: 'Clínica Médica' },
  { id: 'hospital', en: 'Hospital', es: 'Hospital', ru: 'Больница',
    de: 'Krankenhaus', fr: 'Hôpital', it: 'Ospedale', pl: 'Szpital', nl: 'Ziekenhuis', pt: 'Hospital' },
  { id: 'university', en: 'University/Academic Institution', es: 'Universidad/Institución Académica', ru: 'Университет/Академическое Учреждение',
    de: 'Universität/Akademische Einrichtung', fr: 'Université/Institution académique', it: 'Università/Istituzione accademica', pl: 'Uniwersytet/Instytucja akademicka', nl: 'Universiteit/Academische instelling', pt: 'Universidade/Instituição Acadêmica' },
  { id: 'pharmaceutical', en: 'Pharmaceutical Company', es: 'Empresa Farmacéutica', ru: 'Фармацевтическая Компания',
    de: 'Pharmaunternehmen', fr: 'Société pharmaceutique', it: 'Azienda farmaceutica', pl: 'Firma farmaceutyczna', nl: 'Farmaceutisch bedrijf', pt: 'Empresa Farmacêutica' },
  { id: 'veterinary', en: 'Veterinary Clinic', es: 'Clínica Veterinaria', ru: 'Ветеринарная Клиника',
    de: 'Tierklinik', fr: 'Clinique vétérinaire', it: 'Clinica veterinaria', pl: 'Klinika weterynaryjna', nl: 'Dierenkliniek', pt: 'Clínica Veterinária' },
  { id: 'other', en: 'Other Authorized Entity', es: 'Otra Entidad Autorizada', ru: 'Другое Авторизованное Учреждение',
    de: 'Sonstige autorisierte Einrichtung', fr: 'Autre entité autorisée', it: 'Altra entità autorizzata', pl: 'Inna autoryzowana jednostka', nl: 'Andere geautoriseerde entiteit', pt: 'Outra Entidade Autorizada' }
];

const ProfilePage = () => {
  const { user, updateProfile, logout, isAuthenticated, loading } = useAuth();
  const { currentLanguage, t: tg } = useLanguage();
  const navigate = useNavigate();
  
  const [editingSection, setEditingSection] = useState(null);
  const [saveError, setSaveError] = useState('');
  
  const [selectedGoals, setSelectedGoals] = useState(user?.goals || []);
  const [dateOfBirth, setDateOfBirth] = useState(user?.date_of_birth || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  const [phone, setPhone] = useState(user?.phone || '');
  const [organizationName, setOrganizationName] = useState(user?.organization_name || '');
  const [organizationType, setOrganizationType] = useState(user?.organization_type || '');
  const [licenseNumber, setLicenseNumber] = useState(user?.license_number || '');
  const [specialty, setSpecialty] = useState(user?.specialty || '');
  
  const [addressLine1, setAddressLine1] = useState(user?.address_line1 || '');
  const [addressLine2, setAddressLine2] = useState(user?.address_line2 || '');
  const [city, setCity] = useState(user?.city || '');
  const [state, setState] = useState(user?.state || '');
  const [postalCode, setPostalCode] = useState(user?.postal_code || '');
  const [country, setCountry] = useState(user?.country || 'Spain');

  React.useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    const ensureSessionToken = async () => {
      if (isAuthenticated && !getSessionToken()) {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const response = await fetch(`${API_URL}/api/auth/refresh-token`, {
            method: 'POST',
            credentials: 'include',
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.session_token) {
              setSessionToken(data.session_token);
            }
          }
        } catch (err) {
          console.error('ProfilePage: Error refreshing session token:', err);
        }
      }
    };
    
    ensureSessionToken();
  }, [isAuthenticated]);

  const [initialized, setInitialized] = useState(false);

  React.useEffect(() => {
    if (user && !initialized) {
      setSelectedGoals(user.goals || []);
      setDateOfBirth(user.date_of_birth || '');
      setPhone(user.phone || '');
      setOrganizationName(user.organization_name || '');
      setOrganizationType(user.organization_type || '');
      setLicenseNumber(user.license_number || '');
      setSpecialty(user.specialty || '');
      setAddressLine1(user.address_line1 || '');
      setAddressLine2(user.address_line2 || '');
      setCity(user.city || '');
      setState(user.state || '');
      setPostalCode(user.postal_code || '');
      setCountry(user.country || 'Spain');
      setInitialized(true);
    }
  }, [user, initialized]);

  const toggleGoal = (goalId) => {
    setSelectedGoals(prev => 
      prev.includes(goalId) 
        ? prev.filter(g => g !== goalId)
        : [...prev, goalId]
    );
  };

  const handleSaveSection = async (section) => {
    setSaving(true);
    setSaveError('');
    const updates = {};
    
    if (section === 'professional') {
      updates.phone = phone;
      updates.organization_name = organizationName;
      updates.organization_type = organizationType;
      updates.license_number = licenseNumber;
      updates.specialty = specialty;
    } else if (section === 'address') {
      updates.address_line1 = addressLine1;
      updates.address_line2 = addressLine2;
      updates.city = city;
      updates.state = state;
      updates.postal_code = postalCode;
      updates.country = country;
    } else if (section === 'goals') {
      updates.goals = selectedGoals;
      updates.date_of_birth = dateOfBirth;
    }
    
    try {
      const currentToken = getSessionToken();
      if (!currentToken) {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const refreshResponse = await fetch(`${API_URL}/api/auth/refresh-token`, {
            method: 'POST',
            credentials: 'include',
          });
          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            if (refreshData.session_token) {
              setSessionToken(refreshData.session_token);
            }
          }
        } catch (refreshErr) {
          console.error('Failed to refresh token:', refreshErr);
        }
      }
      
      const result = await updateProfile(updates);
      setSaving(false);
      
      if (result.success) {
        if (result.user) {
          if (section === 'professional') {
            setPhone(result.user.phone || '');
            setOrganizationName(result.user.organization_name || '');
            setOrganizationType(result.user.organization_type || '');
            setLicenseNumber(result.user.license_number || '');
            setSpecialty(result.user.specialty || '');
          } else if (section === 'address') {
            setAddressLine1(result.user.address_line1 || '');
            setAddressLine2(result.user.address_line2 || '');
            setCity(result.user.city || '');
            setState(result.user.state || '');
            setPostalCode(result.user.postal_code || '');
            setCountry(result.user.country || 'Spain');
          } else if (section === 'goals') {
            setSelectedGoals(result.user.goals || []);
            setDateOfBirth(result.user.date_of_birth || '');
          }
        }
        setEditingSection(null);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setSaveError(result.error || 'Failed to save. Please try again.');
      }
    } catch (err) {
      console.error('Save error:', err);
      setSaving(false);
      if (err.message && (err.message.includes('Not authenticated') || err.message.includes('401'))) {
        setSaveError('Session expired. Please refresh the page and try again.');
      } else {
        setSaveError(err.message || 'An error occurred. Please try again.');
      }
    }
  };

  const cancelEdit = (section) => {
    setSaveError('');
    if (user) {
      if (section === 'professional') {
        setPhone(user.phone || '');
        setOrganizationName(user.organization_name || '');
        setOrganizationType(user.organization_type || '');
        setLicenseNumber(user.license_number || '');
        setSpecialty(user.specialty || '');
      } else if (section === 'address') {
        setAddressLine1(user.address_line1 || '');
        setAddressLine2(user.address_line2 || '');
        setCity(user.city || '');
        setState(user.state || '');
        setPostalCode(user.postal_code || '');
        setCountry(user.country || 'Spain');
      } else if (section === 'goals') {
        setSelectedGoals(user.goals || []);
        setDateOfBirth(user.date_of_birth || '');
      }
    }
    setEditingSection(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getGoalName = (goal) => {
    return goal[currentLanguage] || goal.en;
  };

  const getOrgTypeName = (orgId) => {
    const org = ORGANIZATION_TYPES.find(o => o.id === orgId);
    return org ? (org[currentLanguage] || org.en) : orgId;
  };

  const translations = {
    en: {
      profile: 'My Profile',
      yourGoals: 'Your Health Goals',
      goalsDesc: 'Select your goals to get personalized product recommendations',
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      saved: 'Saved!',
      logout: 'Logout',
      viewRecommendations: 'View Recommendations',
      dateOfBirth: 'Date of Birth',
      orderHistory: 'Order History',
      businessInfo: 'Professional Information',
      businessInfoDesc: 'Required for order processing and KYC verification',
      phone: 'Phone Number',
      organizationName: 'Organization/Institution Name',
      organizationType: 'Organization Type',
      selectType: 'Select organization type...',
      licenseNumber: 'License/Registration Number',
      specialty: 'Specialty/Department',
      shippingAddress: 'Shipping Address',
      shippingDesc: 'Default address for orders',
      addressLine1: 'Address Line 1',
      addressLine2: 'Address Line 2',
      city: 'City',
      state: 'State/Province',
      postalCode: 'Postal Code',
      country: 'Country',
      notSet: 'Not set',
      verificationStatus: 'Verification Status',
      verified: 'Verified',
      pending_review: 'Under Review',
      not_approved: 'Not Approved'
    },
    es: {
      profile: 'Mi Perfil',
      yourGoals: 'Tus Objetivos de Salud',
      goalsDesc: 'Selecciona tus objetivos para recibir recomendaciones personalizadas',
      save: 'Guardar',
      cancel: 'Cancelar',
      edit: 'Editar',
      saved: '¡Guardado!',
      logout: 'Cerrar Sesión',
      viewRecommendations: 'Ver Recomendaciones',
      dateOfBirth: 'Fecha de Nacimiento',
      orderHistory: 'Historial de Pedidos',
      businessInfo: 'Información Profesional',
      businessInfoDesc: 'Requerido para el procesamiento de pedidos y verificación KYC',
      phone: 'Número de Teléfono',
      organizationName: 'Nombre de la Organización/Institución',
      organizationType: 'Tipo de Organización',
      selectType: 'Seleccione tipo de organización...',
      licenseNumber: 'Número de Licencia/Registro',
      specialty: 'Especialidad/Departamento',
      shippingAddress: 'Dirección de Envío',
      shippingDesc: 'Dirección predeterminada para pedidos',
      addressLine1: 'Dirección Línea 1',
      addressLine2: 'Dirección Línea 2',
      city: 'Ciudad',
      state: 'Provincia',
      postalCode: 'Código Postal',
      country: 'País',
      notSet: 'No establecido',
      verificationStatus: 'Estado de Verificación',
      verified: 'Verificado',
      pending_review: 'En Revisión',
      not_approved: 'No Aprobado'
    },
    ru: {
      profile: 'Мой Профиль',
      yourGoals: 'Ваши Цели Здоровья',
      goalsDesc: 'Выберите цели для персональных рекомендаций',
      save: 'Сохранить',
      cancel: 'Отмена',
      edit: 'Редактировать',
      saved: 'Сохранено!',
      logout: 'Выйти',
      viewRecommendations: 'Смотреть Рекомендации',
      dateOfBirth: 'Дата Рождения',
      orderHistory: 'История Заказов',
      businessInfo: 'Профессиональная Информация',
      businessInfoDesc: 'Требуется для обработки заказов и KYC верификации',
      phone: 'Номер Телефона',
      organizationName: 'Название Организации/Учреждения',
      organizationType: 'Тип Организации',
      selectType: 'Выберите тип организации...',
      licenseNumber: 'Номер Лицензии/Регистрации',
      specialty: 'Специальность/Отдел',
      shippingAddress: 'Адрес Доставки',
      shippingDesc: 'Адрес по умолчанию для заказов',
      addressLine1: 'Адрес Строка 1',
      addressLine2: 'Адрес Строка 2',
      city: 'Город',
      state: 'Область',
      postalCode: 'Почтовый Индекс',
      country: 'Страна',
      notSet: 'Не указано',
      verificationStatus: 'Статус Верификации',
      verified: 'Подтверждено',
      pending_review: 'На Рассмотрении',
      not_approved: 'Не Одобрено'
    },
    de: {
      profile: 'Mein Profil', yourGoals: 'Ihre Gesundheitsziele',
      goalsDesc: 'Wählen Sie Ihre Ziele aus, um personalisierte Produktempfehlungen zu erhalten',
      save: 'Speichern', cancel: 'Abbrechen', edit: 'Bearbeiten', saved: 'Gespeichert!',
      logout: 'Abmelden', viewRecommendations: 'Empfehlungen ansehen',
      dateOfBirth: 'Geburtsdatum', orderHistory: 'Bestellverlauf',
      businessInfo: 'Berufliche Informationen',
      businessInfoDesc: 'Erforderlich für die Auftragsabwicklung und KYC-Verifizierung',
      phone: 'Telefonnummer', organizationName: 'Name der Organisation/Einrichtung',
      organizationType: 'Organisationstyp', selectType: 'Organisationstyp auswählen...',
      licenseNumber: 'Lizenz-/Registrierungsnummer', specialty: 'Fachgebiet/Abteilung',
      shippingAddress: 'Lieferadresse', shippingDesc: 'Standardadresse für Bestellungen',
      addressLine1: 'Adresszeile 1', addressLine2: 'Adresszeile 2',
      city: 'Stadt', state: 'Bundesland', postalCode: 'Postleitzahl', country: 'Land',
      notSet: 'Nicht festgelegt', verificationStatus: 'Verifizierungsstatus',
      verified: 'Verifiziert', pending_review: 'In Prüfung', not_approved: 'Nicht zugelassen'
    },
    fr: {
      profile: 'Mon Profil', yourGoals: 'Vos Objectifs Santé',
      goalsDesc: 'Sélectionnez vos objectifs pour des recommandations personnalisées',
      save: 'Enregistrer', cancel: 'Annuler', edit: 'Modifier', saved: 'Enregistré !',
      logout: 'Se déconnecter', viewRecommendations: 'Voir les recommandations',
      dateOfBirth: 'Date de naissance', orderHistory: 'Historique des commandes',
      businessInfo: 'Informations professionnelles',
      businessInfoDesc: 'Requis pour le traitement des commandes et la vérification KYC',
      phone: 'Numéro de téléphone', organizationName: "Nom de l'organisation/institution",
      organizationType: "Type d'organisation", selectType: "Sélectionner un type d'organisation...",
      licenseNumber: 'Numéro de licence/enregistrement', specialty: 'Spécialité/Département',
      shippingAddress: 'Adresse de livraison', shippingDesc: 'Adresse par défaut pour les commandes',
      addressLine1: 'Adresse ligne 1', addressLine2: 'Adresse ligne 2',
      city: 'Ville', state: 'Région', postalCode: 'Code postal', country: 'Pays',
      notSet: 'Non défini', verificationStatus: 'Statut de vérification',
      verified: 'Vérifié', pending_review: 'En cours de revue', not_approved: 'Non approuvé'
    },
    it: {
      profile: 'Il Mio Profilo', yourGoals: 'I Tuoi Obiettivi di Salute',
      goalsDesc: 'Seleziona i tuoi obiettivi per ricevere raccomandazioni personalizzate',
      save: 'Salva', cancel: 'Annulla', edit: 'Modifica', saved: 'Salvato!',
      logout: 'Esci', viewRecommendations: 'Vedi raccomandazioni',
      dateOfBirth: 'Data di nascita', orderHistory: 'Cronologia ordini',
      businessInfo: 'Informazioni professionali',
      businessInfoDesc: 'Richiesto per la gestione degli ordini e la verifica KYC',
      phone: 'Numero di telefono', organizationName: "Nome dell'organizzazione/istituzione",
      organizationType: 'Tipo di organizzazione', selectType: 'Seleziona tipo di organizzazione...',
      licenseNumber: 'Numero di licenza/registrazione', specialty: 'Specialità/Dipartimento',
      shippingAddress: 'Indirizzo di spedizione', shippingDesc: 'Indirizzo predefinito per gli ordini',
      addressLine1: 'Indirizzo riga 1', addressLine2: 'Indirizzo riga 2',
      city: 'Città', state: 'Provincia', postalCode: 'CAP', country: 'Paese',
      notSet: 'Non impostato', verificationStatus: 'Stato di verifica',
      verified: 'Verificato', pending_review: 'In revisione', not_approved: 'Non approvato'
    },
    pl: {
      profile: 'Mój Profil', yourGoals: 'Twoje Cele Zdrowotne',
      goalsDesc: 'Wybierz swoje cele, aby otrzymać spersonalizowane rekomendacje produktów',
      save: 'Zapisz', cancel: 'Anuluj', edit: 'Edytuj', saved: 'Zapisano!',
      logout: 'Wyloguj się', viewRecommendations: 'Zobacz rekomendacje',
      dateOfBirth: 'Data urodzenia', orderHistory: 'Historia zamówień',
      businessInfo: 'Informacje zawodowe',
      businessInfoDesc: 'Wymagane do realizacji zamówień i weryfikacji KYC',
      phone: 'Numer telefonu', organizationName: 'Nazwa organizacji/instytucji',
      organizationType: 'Typ organizacji', selectType: 'Wybierz typ organizacji...',
      licenseNumber: 'Numer licencji/rejestracji', specialty: 'Specjalność/Dział',
      shippingAddress: 'Adres wysyłki', shippingDesc: 'Domyślny adres dla zamówień',
      addressLine1: 'Adres linia 1', addressLine2: 'Adres linia 2',
      city: 'Miasto', state: 'Województwo', postalCode: 'Kod pocztowy', country: 'Kraj',
      notSet: 'Nie ustawiono', verificationStatus: 'Status weryfikacji',
      verified: 'Zweryfikowano', pending_review: 'W trakcie weryfikacji', not_approved: 'Niezatwierdzony'
    },
    nl: {
      profile: 'Mijn Profiel', yourGoals: 'Uw Gezondheidsdoelen',
      goalsDesc: 'Selecteer uw doelen om gepersonaliseerde productaanbevelingen te ontvangen',
      save: 'Opslaan', cancel: 'Annuleren', edit: 'Bewerken', saved: 'Opgeslagen!',
      logout: 'Uitloggen', viewRecommendations: 'Aanbevelingen bekijken',
      dateOfBirth: 'Geboortedatum', orderHistory: 'Bestelgeschiedenis',
      businessInfo: 'Professionele informatie',
      businessInfoDesc: 'Vereist voor orderverwerking en KYC-verificatie',
      phone: 'Telefoonnummer', organizationName: 'Naam van organisatie/instelling',
      organizationType: 'Type organisatie', selectType: 'Selecteer type organisatie...',
      licenseNumber: 'Licentie-/registratienummer', specialty: 'Specialiteit/Afdeling',
      shippingAddress: 'Verzendadres', shippingDesc: 'Standaardadres voor bestellingen',
      addressLine1: 'Adresregel 1', addressLine2: 'Adresregel 2',
      city: 'Stad', state: 'Provincie', postalCode: 'Postcode', country: 'Land',
      notSet: 'Niet ingesteld', verificationStatus: 'Verificatiestatus',
      verified: 'Geverifieerd', pending_review: 'In behandeling', not_approved: 'Niet goedgekeurd'
    },
    pt: {
      profile: 'Meu Perfil', yourGoals: 'Seus Objetivos de Saúde',
      goalsDesc: 'Selecione seus objetivos para receber recomendações personalizadas',
      save: 'Salvar', cancel: 'Cancelar', edit: 'Editar', saved: 'Salvo!',
      logout: 'Sair', viewRecommendations: 'Ver recomendações',
      dateOfBirth: 'Data de nascimento', orderHistory: 'Histórico de pedidos',
      businessInfo: 'Informações profissionais',
      businessInfoDesc: 'Necessário para processamento de pedidos e verificação KYC',
      phone: 'Número de telefone', organizationName: 'Nome da organização/instituição',
      organizationType: 'Tipo de organização', selectType: 'Selecione o tipo de organização...',
      licenseNumber: 'Número de licença/registro', specialty: 'Especialidade/Departamento',
      shippingAddress: 'Endereço de entrega', shippingDesc: 'Endereço padrão para pedidos',
      addressLine1: 'Endereço linha 1', addressLine2: 'Endereço linha 2',
      city: 'Cidade', state: 'Estado', postalCode: 'CEP', country: 'País',
      notSet: 'Não definido', verificationStatus: 'Status de verificação',
      verified: 'Verificado', pending_review: 'Em análise', not_approved: 'Não aprovado'
    }
  };

  const t = translations[currentLanguage] || translations.en;

  if (loading) {
    return (
      <div className="min-h-screen bg-capsule-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-capsule-teal" />
      </div>
    );
  }

  if (!user) return null;

  const isEditingProfessional = editingSection === 'professional';
  const isEditingAddress = editingSection === 'address';
  const isEditingGoals = editingSection === 'goals';

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />
      
      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="profileGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#profileGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#profileGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-10 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-12 md:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-4 border border-white/20">
              <User className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{tg('profile_account_settings')}</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{t.profile}</h1>
            <p className="text-lg text-gray-300">{tg('profile_manage_subtitle')}</p>
          </div>
        </div>
      </div>
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 -mt-8">
        <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
          {/* Success message */}
          {saved && (
            <div className="bg-capsule-teal/10 border border-capsule-teal/30 text-capsule-teal px-4 py-3 rounded-xl flex items-center justify-center gap-2 text-sm sm:text-base">
              <Check className="w-5 h-5" />
              {t.saved}
            </div>
          )}

          {/* Error message */}
          {saveError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center justify-center gap-2 text-sm sm:text-base">
              <X className="w-5 h-5" />
              {saveError}
            </div>
          )}

          {/* Profile Card */}
          <Card className="shadow-lg border-0 rounded-2xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <User className="w-5 h-5" />
                {t.profile}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              {/* User Info */}
              <div className="flex items-center gap-4">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    className="w-16 h-16 rounded-full border-2 border-capsule-teal/30"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal to-capsule-aqua flex items-center justify-center text-white text-2xl font-bold">
                    {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900">{user.name}</h2>
                  <div className="flex items-center gap-1 text-gray-500">
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{user.email}</span>
                  </div>
                </div>
                {/* Verification Status Badge */}
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-500 mb-1">{t.verificationStatus}</span>
                  {user.verification_status === 'verified' && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700 border border-green-200">
                      <Check className="w-4 h-4" />
                      {t.verified}
                    </span>
                  )}
                  {user.verification_status === 'pending_review' && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700 border border-amber-200">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t.pending_review}
                    </span>
                  )}
                  {user.verification_status === 'not_approved' && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700 border border-red-200">
                      <X className="w-4 h-4" />
                      {t.not_approved}
                    </span>
                  )}
                  {!user.verification_status && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700 border border-amber-200">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t.pending_review}
                    </span>
                  )}
                </div>
              </div>

              {/* Order History Link */}
              <Link to="/orders">
                <Button
                  variant="outline"
                  className="w-full border-capsule-teal/30 text-capsule-teal hover:bg-capsule-teal/10 rounded-xl"
                  data-testid="order-history-btn"
                >
                  <Package className="w-4 h-4 mr-2" />
                  {t.orderHistory}
                </Button>
              </Link>

              {/* Logout Button */}
              <Button
                onClick={handleLogout}
                variant="outline"
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t.logout}
              </Button>
            </CardContent>
          </Card>

          {/* Professional Information Card */}
          <Card className="shadow-lg border-0 rounded-2xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-capsule-teal" />
                  {t.businessInfo}
                </CardTitle>
                <p className="text-gray-500 text-sm">{t.businessInfoDesc}</p>
              </div>
              {!isEditingProfessional && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setEditingSection('professional')}
                  className="text-capsule-teal hover:text-capsule-teal-dark hover:bg-capsule-teal/10"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  {t.edit}
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Phone */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-capsule-teal" />
                  {t.phone}
                </Label>
                {isEditingProfessional ? (
                  <PhoneInput
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                ) : (
                  <p className={`p-2 bg-gray-50 rounded-lg ${!phone ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                    {phone || t.notSet}
                  </p>
                )}
              </div>

              {/* Organization Name */}
              <div className="space-y-2">
                <Label>{t.organizationName}</Label>
                {isEditingProfessional ? (
                  <Input
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder="e.g., Research Lab Madrid"
                    className="rounded-lg"
                  />
                ) : (
                  <p className={`p-2 bg-gray-50 rounded-lg ${!organizationName ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                    {organizationName || t.notSet}
                  </p>
                )}
              </div>

              {/* Organization Type */}
              <div className="space-y-2">
                <Label>{t.organizationType}</Label>
                {isEditingProfessional ? (
                  <select
                    value={organizationType}
                    onChange={(e) => setOrganizationType(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-capsule-teal focus:border-capsule-teal"
                  >
                    <option value="">{t.selectType}</option>
                    {ORGANIZATION_TYPES.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org[currentLanguage] || org.en}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className={`p-2 bg-gray-50 rounded-lg ${!organizationType ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                    {organizationType ? getOrgTypeName(organizationType) : t.notSet}
                  </p>
                )}
              </div>

              {/* License Number */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-capsule-teal" />
                  {t.licenseNumber}
                </Label>
                {isEditingProfessional ? (
                  <Input
                    value={licenseNumber}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                    placeholder="e.g., LAB-2024-001"
                    className="rounded-lg"
                  />
                ) : (
                  <p className={`p-2 bg-gray-50 rounded-lg ${!licenseNumber ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                    {licenseNumber || t.notSet}
                  </p>
                )}
              </div>

              {/* Specialty */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-capsule-teal" />
                  {t.specialty}
                </Label>
                {isEditingProfessional ? (
                  <Input
                    value={specialty}
                    onChange={(e) => setSpecialty(e.target.value)}
                    placeholder="e.g., Biomedical Research"
                    className="rounded-lg"
                  />
                ) : (
                  <p className={`p-2 bg-gray-50 rounded-lg ${!specialty ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                    {specialty || t.notSet}
                  </p>
                )}
              </div>

              {/* Edit/Save buttons */}
              {isEditingProfessional && (
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => cancelEdit('professional')}
                    className="flex-1 rounded-xl"
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t.cancel}
                  </Button>
                  <Button 
                    onClick={() => handleSaveSection('professional')}
                    className="flex-1 bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white rounded-xl"
                    disabled={saving}
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    {t.save}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Shipping Address Card */}
          <Card className="shadow-lg border-0 rounded-2xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-capsule-teal" />
                  {t.shippingAddress}
                </CardTitle>
                <p className="text-gray-500 text-sm">{t.shippingDesc}</p>
              </div>
              {!isEditingAddress && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setEditingSection('address')}
                  className="text-capsule-teal hover:text-capsule-teal-dark hover:bg-capsule-teal/10"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  {t.edit}
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditingAddress ? (
                <>
                  <div className="space-y-2">
                    <Label>{t.addressLine1}</Label>
                    <Input
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      placeholder={tg('address_street_address')}
                      className="rounded-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t.addressLine2}</Label>
                    <Input
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      placeholder={tg('address_apartment_suite')}
                      className="rounded-lg"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t.city}</Label>
                      <Input
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="rounded-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t.state}</Label>
                      <Input
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        className="rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t.postalCode}</Label>
                      <Input
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        className="rounded-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t.country}</Label>
                      <Input
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button 
                      variant="outline" 
                      onClick={() => cancelEdit('address')}
                      className="flex-1 rounded-xl"
                    >
                      <X className="w-4 h-4 mr-2" />
                      {t.cancel}
                    </Button>
                    <Button 
                      onClick={() => handleSaveSection('address')}
                      className="flex-1 bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white rounded-xl"
                      disabled={saving}
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      {t.save}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="p-3 bg-gray-50 rounded-lg">
                  {addressLine1 ? (
                    <>
                      <p className="text-gray-700">{addressLine1}</p>
                      {addressLine2 && <p className="text-gray-700">{addressLine2}</p>}
                      <p className="text-gray-700">{city}{state ? `, ${state}` : ''} {postalCode}</p>
                      <p className="text-gray-700">{country}</p>
                    </>
                  ) : (
                    <p className="text-gray-400 italic">{t.notSet}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Goals Card */}
          <Card className="shadow-lg border-0 rounded-2xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-capsule-teal" />
                  {t.yourGoals}
                </CardTitle>
                <p className="text-gray-500 text-sm">{t.goalsDesc}</p>
              </div>
              {!isEditingGoals && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setEditingSection('goals')}
                  className="text-capsule-teal hover:text-capsule-teal-dark hover:bg-capsule-teal/10"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  {t.edit}
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date of Birth */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-capsule-teal" />
                  {t.dateOfBirth}
                </Label>
                {isEditingGoals ? (
                  <Input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="max-w-xs rounded-lg"
                  />
                ) : (
                  <p className={`p-2 bg-gray-50 rounded-lg max-w-xs ${!dateOfBirth ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                    {dateOfBirth || t.notSet}
                  </p>
                )}
              </div>

              {/* Goal Selection Grid */}
              <div className="grid grid-cols-2 gap-3">
                {GOALS.map((goal) => (
                  <button
                    key={goal.id}
                    onClick={() => isEditingGoals && toggleGoal(goal.id)}
                    disabled={!isEditingGoals}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      selectedGoals.includes(goal.id)
                        ? 'border-capsule-teal bg-capsule-teal/10 shadow-md'
                        : isEditingGoals 
                          ? 'border-gray-200 hover:border-capsule-teal/50 hover:bg-capsule-teal/5'
                          : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                    }`}
                    data-testid={`goal-${goal.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-2xl">{goal.icon}</span>
                      {selectedGoals.includes(goal.id) && (
                        <Check className="w-5 h-5 text-capsule-teal" />
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-900 mt-1 block">
                      {getGoalName(goal)}
                    </span>
                  </button>
                ))}
              </div>

              {/* Edit/Save buttons for goals */}
              {isEditingGoals ? (
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => cancelEdit('goals')}
                    className="flex-1 rounded-xl"
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t.cancel}
                  </Button>
                  <Button 
                    onClick={() => handleSaveSection('goals')}
                    className="flex-1 bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white rounded-xl"
                    disabled={saving}
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    {t.save}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => navigate('/recommendations')}
                  variant="outline"
                  className="w-full border-capsule-teal/30 text-capsule-teal hover:bg-capsule-teal/10 rounded-xl"
                  data-testid="view-recommendations-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t.viewRecommendations}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProfilePage;
